import { useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { TemplateLossNotice } from '@/components/settings/TemplateCheckSummary'
import { useExportTemplatesStore } from '@/stores/export-templates-store'
import {
  isUnsafePlaceholder,
  templateFixHowTo,
  templateLossSentence,
  type TemplateIssue,
} from '@/lib/template-check'

/**
 * Stops an export whose template would take some of its own text with it
 * (Kapil's D4: warn and ask, never silent deletion) — a template with no
 * content placeholder, or one whose placeholder sits in a table cell, a text
 * box or beside other words. The user can fix the template, or export anyway,
 * which exports exactly as it always has.
 */
export function TemplateIssueDialog({
  issue,
  onContinue,
  onClose,
}: {
  issue: TemplateIssue | null
  /** Export anyway, acknowledging the loss. */
  onContinue: () => void
  onClose: () => void
}) {
  const navigate = useNavigate()
  const setFixGuide = useExportTemplatesStore((s) => s.setFixGuide)
  const report = issue?.report
  const loss = report ? templateLossSentence(report) : null
  const unsafe = !!report && isUnsafePlaceholder(report)

  return (
    <Dialog open={issue !== null} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {unsafe ? 'The placeholder in this template is in the wrong place' : 'This template has no content placeholder'}
          </DialogTitle>
          <DialogDescription>
            {`Exporting with "${issue?.templateName}" replaces part of the template itself.`}
          </DialogDescription>
        </DialogHeader>

        {report && loss && <TemplateLossNotice sentence={loss} howTo={templateFixHowTo(report)} />}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              // The templates page shows the how-to for this template.
              if (issue && report) setFixGuide({ templateName: issue.templateName, howTo: templateFixHowTo(report) })
              onClose()
              void navigate('/app/settings/export-templates')
            }}
          >
            {unsafe ? 'Fix the placeholder' : 'Add the placeholder'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onContinue}>Export anyway</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The warning for an export the server refused (409 TEMPLATE_CONTENT_LOSS),
 * from any export door: the document export, the Submission Pack, Save to
 * SharePoint. Mounted once in the app shell; "Export anyway" runs the retry
 * the door handed over with the error (`routeTemplateExportError`).
 */
export function TemplateIssueHost() {
  const issue = useExportTemplatesStore((s) => s.templateIssue)
  const retry = useExportTemplatesStore((s) => s.templateIssueRetry)
  const clear = useExportTemplatesStore((s) => s.clearTemplateIssue)

  return (
    <TemplateIssueDialog
      issue={issue}
      onClose={clear}
      onContinue={() => {
        clear()
        retry?.()
      }}
    />
  )
}
